import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import MarketDiscussions from './MarketDiscussions';
import { commoditiesOf, commodityPrice, commodityChange } from './Commodities';
import { priceChart } from './PriceChart';
import { loadMarkets, stocksOf, marketStatus, ratesOf, prevRatesOf, naira, pct, tone, updatedText, stockHref, kpiValue, kpiPeriod } from '../util';

/** The figures that set the cost and value of money in Nigeria (Data Hub), in this order; the naira has its own card */
const MONEY_KEYS = ['mpr', 'cpi_headline_yoy', 'tbill_364', 'nofr', 'external_reserves'];
const FX = [['USD', '$'], ['GBP', '£'], ['EUR', '€'], ['CAD', 'C$']];

/**
 * /markets: every market in one place, each leading to its own page. A search (companies and commodities), the
 * figures that set the cost of money, the naira, then a card per market: the NGX (/markets/ngx), commodities
 * (/commodities) and real estate (coming soon). The app's Markets screen is laid out the same way.
 */
export default class MarketsHubPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.markets = null;
    this.error = null;
    this.kpis = null;
    this.points = [];
    this.q = '';
    app.setTitle('Markets today');
    loadMarkets().then(d => this.markets = d).catch(() => this.error = 'Could not load market data. Please try again shortly.').then(() => m.redraw());
    const api = app.forum.attribute('apiUrl');
    app.request({
      method: 'GET',
      url: api + '/datahub/kpis?keys=' + MONEY_KEYS.join(','),
      errorHandler: () => {}
    }).then(r => this.kpis = r && r.kpis && r.kpis.length ? r.kpis : null).catch(() => this.kpis = null).then(() => m.redraw());
    app.request({
      method: 'GET',
      url: api + '/voucha-markets/overall',
      errorHandler: () => {}
    }).then(r => this.points = r && r.points || []).catch(() => this.points = []).then(() => m.redraw());
  }
  view() {
    const words = this.q.trim().toLowerCase();
    return m('.VouchaMarkets.VouchaHub', [
    // phones: the page's name in the header bar (Flarum places .App-titleControl there)
    m('h4.App-titleControl.App-titleControl--text', 'Markets'), m('header.VouchaMk-head', [m('h1', 'Markets'), m('p.helpText', 'Nigerian shares, commodity prices, the naira and the figures that set the cost of money.')]), m('.VouchaHub-search', [m('i.fas.fa-search', {
      'aria-hidden': 'true'
    }), m('input', {
      type: 'search',
      value: this.q,
      placeholder: 'Search companies or commodities',
      'aria-label': 'Search companies or commodities',
      oninput: e => this.q = e.target.value
    })]), this.error ? m('.VouchaMarkets-error', this.error) : null, !this.markets && !this.error ? m('.VouchaMarkets-loading', 'Loading market data…') : null, words ? this.results(words) : [this.money(), this.naira(), this.cards(), m(MarketDiscussions, {
      title: 'Talk about the markets',
      empty: 'No discussions yet. Start one in Finance & Business.'
    })], m('p.VouchaMk-source', 'Share prices from the Nigerian Exchange (via NGXPulse); commodities from Alpha Vantage and the IMF; key figures from the Voucha Data Hub. Not investment advice.')]);
  }

  /** What the search finds: companies by symbol or name, and commodities */
  results(words) {
    if (!this.markets) return null;
    const stocks = stocksOf(this.markets).filter(s => (s.symbol + ' ' + (s.name || '')).toLowerCase().includes(words)).slice(0, 12);
    const commodities = commoditiesOf(this.markets).filter(c => (c.key + ' ' + c.name).toLowerCase().includes(words));
    if (!stocks.length && !commodities.length) return m('section.VouchaMarkets-section', m('p.VouchaMarkets-note', 'Nothing matches “' + this.q.trim() + '”.'));
    const row = (key, href, title, sub, price, change) => m('li', {
      key
    }, m(Link, {
      href
    }, [m('span.VouchaMk-moverName', [m('strong', title), m('small', sub)]), m('span.VouchaMk-moverNum', [m('span', price), change !== null ? m('b', {
      className: tone(change)
    }, pct(change)) : null])]));
    return m('section.VouchaMarkets-section', [m('h2', 'Results'), m('ol.VouchaMk-movers.VouchaHub-results', [stocks.map(s => row(s.symbol, stockHref(s.symbol), s.symbol, (s.name || '') + ' · NGX', naira(s.current_price), s.change_percent)), commodities.map(c => row(c.key, '/commodities#' + c.key, c.name, 'Commodity', commodityPrice(c), commodityChange(c)))])]);
  }

  /** The Data Hub's figures that set the cost and value of money, as tiles */
  money() {
    if (!this.kpis) return null;
    const order = k => MONEY_KEYS.indexOf(k.key);
    return m('section.VouchaHub-block', [m('.VouchaMk-sectionHead', [m('h2', 'Money today'), m(Link, {
      href: '/data-hub',
      className: 'VouchaHub-more'
    }, 'Data Hub →')]), m('p.VouchaMarkets-note', 'The rates that set the cost and value of money in Nigeria.'), m('.VouchaHub-tiles', this.kpis.slice().sort((a, b) => order(a) - order(b)).map(k => {
      const t = !k.change || !k.better ? 'is-neutral' : k.change > 0 === (k.better === 'up') ? 'is-good' : 'is-bad';
      const change = k.change === null || k.change === undefined || k.change === 0 ? null : (k.change > 0 ? '▲ ' : '▼ ') + Math.abs(k.change) + (k.changeUnit === 'pts' ? ' pts' : '%');
      return m(Link, {
        key: k.key,
        className: 'VouchaHub-tile',
        href: '/data-hub/' + k.key,
        title: k.previousPeriod ? 'Change vs ' + kpiPeriod(k.previousPeriod) : ''
      }, [m('span.VouchaHub-tileLabel', k.label), m('strong', kpiValue(k.value, k.unit)), m('span.VouchaHub-tileNote', [k.period ? kpiPeriod(k.period) : '', change ? [k.period ? ' · ' : '', m('span.VouchaMarkets-kpiChange.' + t, change)] : null])]);
    }))]);
  }

  /** The naira against the main currencies, with the change since the day before (more naira per unit is a weaker naira) */
  naira() {
    const rates = ratesOf(this.markets);
    const prev = prevRatesOf(this.markets);
    const list = FX.filter(([k]) => Number(rates[k]) > 0);
    if (!list.length) return null;
    return m('section.VouchaHub-block', [m('.VouchaMk-sectionHead', [m('h2', 'The naira'), m(Link, {
      href: '/salary-converter',
      className: 'VouchaHub-more'
    }, 'Convert a salary →')]), m('.VouchaHub-tiles.VouchaHub-tiles--fx', list.map(([k, sym]) => {
      const r = Number(rates[k]);
      const p = Number(prev[k]);
      const ch = p > 0 ? (r - p) / p * 100 : null;
      // a rise is the naira weakening: shown in the "down" colour
      const t = tone(ch) === 'is-up' ? 'is-down' : tone(ch) === 'is-down' ? 'is-up' : '';
      return m('.VouchaHub-tile', {
        key: k
      }, [m('span.VouchaHub-tileLabel', sym + '1 (' + k + ')'), m('strong', naira(r)), ch !== null ? m('span.VouchaHub-tileNote', m('b', {
        className: t
      }, pct(ch))) : m('span.VouchaHub-tileNote', 'Today')]);
    }))]);
  }

  /** A card for each market, leading to its page */
  cards() {
    if (!this.markets) return null;
    return m('section.VouchaHub-block', [m('h2', 'Markets'), m('.VouchaHub-cards', [this.ngx(), this.commodities(), this.realEstate()])]);
  }
  ngx() {
    const stocks = stocksOf(this.markets);
    const status = marketStatus(this.markets);
    let cap = 0;
    let up = 0;
    let down = 0;
    stocks.forEach(s => {
      cap += Number(s.market_cap) || 0;
      if (tone(s.change_percent) === 'is-up') up++;else if (tone(s.change_percent) === 'is-down') down++;
    });
    const moved = stocks.filter(s => s.change_percent !== null && s.change_percent !== undefined);
    const best = moved.slice().sort((a, b) => Number(b.change_percent) - Number(a.change_percent))[0];
    const worst = moved.slice().sort((a, b) => Number(a.change_percent) - Number(b.change_percent))[0];
    const pts = this.points;
    const from = pts.length > 1 ? pts[Math.max(0, pts.length - 8)] : null;
    const move = from && from.value > 0 ? (pts[pts.length - 1].value - from.value) / from.value * 100 : null;
    return m(Link, {
      className: 'VouchaHub-card',
      href: app.route('voucha.markets.ngx')
    }, [this.cardHead('fa-chart-line', 'is-ngx', 'Nigerian stocks (NGX)', (status ? 'Market ' + status.toLowerCase() + ' · ' : '') + updatedText(this.markets.stocks && this.markets.stocks.updated_at)), stocks.length ? [m('.VouchaHub-big', [m('strong', naira(cap, true)), move !== null ? m('b', {
      className: tone(move)
    }, pct(move) + ' over 7 trading days') : null]), pts.length > 2 ? m('.VouchaHub-chart', m.trust(priceChart(pts.map(p => ({
      date: p.date,
      close: Number(p.value)
    })), v => naira(v, true)))) : null, m('p.VouchaHub-line', [m('b.is-up', up + ' up'), ' · ', m('b.is-down', down + ' down'), ' · ' + (stocks.length - up - down) + ' unchanged']), best && tone(best.change_percent) === 'is-up' ? m('p.VouchaHub-line', ['Top: ', m('strong', best.symbol), ' ', m('b.is-up', pct(best.change_percent)), worst && tone(worst.change_percent) === 'is-down' ? ['   Bottom: ', m('strong', worst.symbol), ' ', m('b.is-down', pct(worst.change_percent))] : null]) : null] : m('p.VouchaMarkets-note', 'Share prices are not available yet.'), m('span.VouchaHub-go', 'Share prices, movers and every listed company →')]);
  }
  commodities() {
    const all = commoditiesOf(this.markets);
    const pick = ['brent', 'gold', 'cocoa'].map(k => all.find(c => c.key === k)).filter(Boolean);
    return m(Link, {
      className: 'VouchaHub-card',
      href: app.route('voucha.commodities')
    }, [this.cardHead('fa-oil-can', 'is-commodities', 'Commodities', 'Oil, gas, metals and farm produce · ' + updatedText(this.markets.commodities && this.markets.commodities.updated_at)), pick.length ? m('.VouchaHub-minis', pick.map(c => {
      const ch = commodityChange(c);
      return m('.VouchaHub-mini', {
        key: c.key
      }, [m('span', c.name), m('strong', commodityPrice(c)), ch !== null ? m('b', {
        className: tone(ch)
      }, pct(ch)) : null]);
    })) : m('p.VouchaMarkets-note', 'Commodity prices are not available yet.'), m('span.VouchaHub-go', 'All commodity prices, in dollars and naira →')]);
  }
  realEstate() {
    return m('.VouchaHub-card.is-soon', [this.cardHead('fa-building', 'is-soon', 'Real estate', 'Rents and property prices across Nigeria', m('span.VouchaHub-soon', 'Coming soon'))]);
  }
  cardHead(icon, cls, title, sub, right) {
    return m('.VouchaHub-cardHead', [m('span.VouchaHub-badge', {
      className: cls
    }, m('i.fas.' + icon, {
      'aria-hidden': 'true'
    })), m('span.VouchaHub-cardTitle', [m('strong', title), m('small', sub)]), right || null]);
  }
}
flarum.reg.add('voucha-markets', 'forum/components/MarketsHubPage', MarketsHubPage);